import React, { useEffect, useState } from "react";
import {
  Button,
  Grid,
  Pagination,
  Paper,
  Stack,
  Typography,
  Box,
  Card,
  Divider,
  Chip,
  TextField,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import { alpha } from "@mui/system";
import { styled } from "@mui/material/styles";
import TableItem from "../../../components/TableItem";
import {
  getWarehouseData,
  getWarehousesByType,
  getWarehouseTypes,
} from "../../../services/Warehouse";
import WarehouseForm from "./WarehouseForm";
import CircularProgress from "@mui/material/CircularProgress";
import { useHistory } from "react-router";
import InputField from "../../../FormComponents/InputField";
import Alart from "../../../components/Alart";
import AddIcon from "@mui/icons-material/Add";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import { RoleBasedRender } from "../../../components/RoleBaseRender";

// Custom styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)",
}));

const HeaderCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderRadius: theme.shape.borderRadius * 1.5,
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(3),
  boxShadow: "0 8px 16px 0 rgba(0,0,0,0.1)",
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 1.5,
  textTransform: "none",
  fontWeight: 600,
  boxShadow: "0 4px 12px 0 rgba(0,0,0,0.1)",
}));

const FilterContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius * 1.5,
  backgroundColor: alpha(theme.palette.primary.light, 0.05),
  marginBottom: theme.spacing(3),
}));

const PaginationContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  padding: theme.spacing(2),
  marginTop: theme.spacing(2),
}));

const StatusChip = styled(Chip)(({ status, theme }) => ({
  backgroundColor:
    status === "Active"
      ? alpha(theme.palette.success.main, 0.1)
      : alpha(theme.palette.error.main, 0.1),
  color:
    status === "Active" ? theme.palette.success.main : theme.palette.error.main,
  fontWeight: 600,
  borderRadius: theme.shape.borderRadius,
}));

let _warehouseListState = null;

function Warehouses() {
  const history = useHistory();

  // State management
  const [rows, setRows] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openForm, setOpenForm] = useState(false);
  const [warehouseTypes, setWarehouseTypes] = useState([]);
  const [error, setErrors] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  // Disable warehouse alert state
  const [openDisableWarning, setOpenDisableWarning] = useState(false);
  const [msgDisableWarning, setMsgDisableWarning] = useState({
    title: "Warning",
    content: "Are you sure you want to disable this warehouse?",
  });
  const [disableItemId, setDisableItemId] = useState(null);

  // Table columns configuration
  const columns = [
    { id: "name", label: "Name", minWidth: 150 },
    { id: "type", label: "Type", minWidth: 120 },
    {
      id: "status",
      label: "Status",
      minWidth: 100,
      format: (value) => (
        <StatusChip
          label={value}
          status={value}
          size="small"
          variant="filled"
        />
      ),
    },
    { id: "action", label: "Actions", minWidth: 120 },
  ];

  // Create data row factory function
  function createData(name, type, status, action, warehouse_id) {
    return { name, type, status, action, warehouse_id };
  }

  // Filter state
  const [filter, setFilter] = useState({
    _name: "",
    _type: "",
    _isActive: "",
  });

  // Initial data load
  useEffect(() => {
    fetchWarehouseTypes();
    if (_warehouseListState) {
      const { page: savedPage, filter: savedFilter } = _warehouseListState;
      _warehouseListState = null;
      setPage(savedPage);
      setFilter(savedFilter);
      fetchData(savedFilter, savedPage);
    } else {
      fetchData(filter, page);
    }
  }, []);

  // Pagination handler
  const handleChange = async (event, value) => {
    setPage(value - 1);
    await fetchData(filter, value - 1);
  };

  // Fetch warehouse types
  const fetchWarehouseTypes = async () => {
    try {
      let result = await getWarehouseTypes();
      if (result !== "Something went wrong...") {
        setWarehouseTypes(result);
      } else {
        setErrors({
          ...error,
          _type: "Something went wrong when fetching warehouse types",
        });
      }
    } catch (err) {
      console.error("Error fetching warehouse types:", err);
      setErrors({
        ...error,
        _type: "Error fetching warehouse types",
      });
    }
  };

  // Fetch warehouse data
  const fetchData = async (warehouseFilter, pageNo) => {
    setLoading(true);
    try {
      let result = await getWarehouseData(pageNo, rowsPerPage, warehouseFilter);
      if (result !== "Something went wrong...") {
        let warehouseSet = result.content;

        // Set total rows and pages
        setTotalPages(result.totalPages);
        setTotalRows(result.totalElements);

        const newSet = [];
        if (warehouseSet && warehouseSet.length > 0) {
          for (let x = 0; x < warehouseSet.length; x++) {
            // Set data in new set list to display in the table
            newSet.push(
              createData(
                warehouseSet[x].warehouseName,
                warehouseSet[x].warehouseType,
                warehouseSet[x].isActive ? "Active" : "Inactive",
                warehouseSet[x].warehouseId
              )
            );
          }
        }

        // Set rows to table
        setRows(newSet);
      } else {
        setErrors({
          ...error,
          _warehouse: "Something went wrong when fetching warehouses",
        });
      }
    } catch (err) {
      console.error("Error fetching warehouse data:", err);
      setErrors({
        ...error,
        _warehouse: "Error fetching warehouses",
      });
    } finally {
      setLoading(false);
    }
  };

  // Action handler for table actions
  const handleAction = async (event, id) => {
    switch (event) {
      case "view":
        _warehouseListState = { page, filter };
        history.push("/template/customer_view/" + id);
        break;
      case "edit":
        history.push("/template/warehouse_update/" + id);
        break;
      case "disable":
        disableProduct(id);
        break;
      default:
        return;
    }
  };

  // Disable warehouse function
  const disableProduct = async (id) => {
    setDisableItemId(id);
    setOpenDisableWarning(true);
  };

  // Disable warehouse alert action buttons
  function DisableWarningActionButtons() {
    return (
      <>
        <ActionButton
          variant="contained"
          color="error"
          onClick={async () => {
            // await DiactivateItemById(disableItemId);
            setOpenDisableWarning(false);
            await fetchData(filter, page);
          }}
        >
          Disable
        </ActionButton>
        <ActionButton
          variant="outlined"
          onClick={() => {
            setOpenDisableWarning(false);
          }}
        >
          Cancel
        </ActionButton>
      </>
    );
  }

  // Reset filters
  const resetFilters = () => {
    setFilter({
      _name: "",
      _type: "",
      _isActive: "",
    });
  };

  // Handle filter change
  const handleFilterChange = (name, val) => {
    setFilter({
      ...filter,
      [name]: val,
    });
  };

  // Toggle filter visibility
  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  return (
    <>
      <Alart
        open={openDisableWarning}
        message={msgDisableWarning}
        actions={<DisableWarningActionButtons />}
      />
      <StyledPaper elevation={0}>
        {openForm ? (
          <WarehouseForm setOpenForm={setOpenForm} />
        ) : (
          <>
            <HeaderCard>
              <Grid container alignItems="center" spacing={2}>
                <Grid item>
                  <WarehouseIcon fontSize="large" />
                </Grid>
                <Grid item xs>
                  <Typography variant="h5" fontWeight="bold">
                    Warehouses Management
                  </Typography>
                  <Typography variant="body2">
                    View and manage your warehouse locations and settings
                  </Typography>
                </Grid>
                <Grid item>
                  <RoleBasedRender
                    allowedRoles={["WAREHOUSE_CREATE", "ROLE_ADMIN"]}
                  >
                    <ActionButton
                      variant="contained"
                      color="primary"
                      startIcon={<AddIcon />}
                      onClick={() => setOpenForm(true)}
                    >
                      Add Warehouse
                    </ActionButton>
                  </RoleBasedRender>
                </Grid>
              </Grid>
            </HeaderCard>

            <Box
              mb={3}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <TextField
                placeholder="Search by warehouse name..."
                variant="outlined"
                size="small"
                sx={{ width: "40%" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                onChange={(e) => {
                  handleFilterChange("_name", e.target.value);
                  if (
                    e.target.value.length > 2 ||
                    e.target.value.length === 0
                  ) {
                    fetchData({ ...filter, _name: e.target.value }, page);
                  }
                }}
              />
              <Stack direction="row" spacing={2}>
                <Tooltip title="Toggle Filters">
                  <ActionButton
                    variant="outlined"
                    color="primary"
                    startIcon={<FilterListIcon />}
                    onClick={toggleFilters}
                  >
                    Filters
                  </ActionButton>
                </Tooltip>
                <Chip
                  label={`${totalRows} Warehouses`}
                  variant="outlined"
                  color="primary"
                />
              </Stack>
            </Box>

            {showFilters && (
              <FilterContainer>
                <Typography variant="subtitle2" fontWeight="bold" mb={2}>
                  Advanced Filters
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <InputField
                      name="_name"
                      value={filter._name}
                      onChange={(event, newInputValue) =>
                        handleFilterChange(event, newInputValue)
                      }
                      type="text"
                      label="Warehouse Name"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <InputField
                      name="_type"
                      value={filter._type}
                      onChange={(event, newInputValue) =>
                        handleFilterChange(event, newInputValue)
                      }
                      type="text"
                      label="Warehouse Type"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <InputField
                      name="_isActive"
                      value={filter._isActive}
                      onChange={(event, newInputValue) =>
                        handleFilterChange(event, newInputValue)
                      }
                      type="text"
                      label="Status"
                      fullWidth
                    />
                  </Grid>
                </Grid>
                <Box mt={2} display="flex" justifyContent="flex-end">
                  <Stack direction="row" spacing={2}>
                    <Tooltip title="Reset Filters">
                      <ActionButton
                        variant="outlined"
                        color="secondary"
                        startIcon={<RestartAltIcon />}
                        onClick={() => {
                          resetFilters();
                          fetchData({}, page);
                        }}
                      >
                        Reset
                      </ActionButton>
                    </Tooltip>
                    <Tooltip title="Apply Filters">
                      <ActionButton
                        variant="contained"
                        color="primary"
                        onClick={() => fetchData(filter, page)}
                      >
                        Apply Filters
                      </ActionButton>
                    </Tooltip>
                  </Stack>
                </Box>
              </FilterContainer>
            )}

            {error?._warehouse && (
              <Box
                sx={{
                  mb: 2,
                  p: 2,
                  bgcolor: alpha("#f44336", 0.1),
                  borderRadius: 1,
                }}
              >
                <Typography variant="body2" color="error" fontWeight="medium">
                  {error._warehouse}
                </Typography>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            {isLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
                <CircularProgress />
              </Box>
            ) : rows.length === 0 ? (
              <Box sx={{ textAlign: "center", p: 5 }}>
                <Typography variant="body1" color="textSecondary">
                  No warehouses found. Try adjusting your filters or add a new
                  warehouse.
                </Typography>
              </Box>
            ) : (
              <TableItem
                columns={columns}
                rows={rows}
                page={page}
                setPage={setPage}
                tablePagin={false}
                totalPages={totalPages}
                handleAction={handleAction}
                showActions={{
                  view: "Warehouse view",
                  edit: "Warehouse edit",
                  disable: "Warehouse diactvate",
                }}
                actionRoles={{
                  view: ["WAREHOUSE_VIEW", "ROLE_ADMIN"],
                  edit: ["WAREHOUSE_UPDATE", "ROLE_ADMIN"],
                  disable: ["WAREHOUSE_DELETE", "ROLE_ADMIN"],
                }}
                tableProps={{
                  sx: {
                    "& .MuiTableRow-root:hover": {
                      backgroundColor: alpha("#000", 0.04),
                    },
                  },
                }}
              />
            )}

            {totalPages > 1 && (
              <PaginationContainer>
                <Pagination
                  count={totalPages}
                  page={page + 1}
                  variant="outlined"
                  shape="rounded"
                  color="primary"
                  onChange={handleChange}
                  size="medium"
                />
              </PaginationContainer>
            )}
          </>
        )}
      </StyledPaper>
    </>
  );
}

export default Warehouses;
